import React from 'react';
import { useTranslation } from 'react-i18next';
import { H4, P } from 'design-system-web';
import { SearchBar } from 'components';
import type { CustomFieldsListLayoutProps, VisitorConfigurationTabProps } from '../../types';
import { CustomFieldsListBody } from './CustomFieldsListBody';
import { useCustomFieldsTabModel } from './useCustomFieldsTabModel';

import styles from './styles.scss';

const NO_ORGANIZATION_KEY = 'web.admin.visitorManagement.customField.error.noOrganization';

function CustomFieldsListLayout({
  searchValue,
  onSearchChange,
  filteredRows,
  isListLoading,
  configsCalled,
  configsError,
}: CustomFieldsListLayoutProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <H4>{t('web.admin.visitorManagement.configuration.tab.customFields')}</H4>
          <div className={styles.description}>
            <P>{t('web.admin.visitorManagement.configuration.tab.customFields.description')}</P>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionToolbar}>
          <SearchBar
            className={styles.searchBlock}
            searchOptions={{ search: searchValue }}
            onSearchChange={onSearchChange}
            searchInputPlaceholder={t(
              'web.admin.visitorManagement.customField.search.placeholder'
            )}
            testId="custom-field-search"
          />
        </div>
        <CustomFieldsListBody
          isListLoading={isListLoading}
          configsCalled={configsCalled}
          configsError={configsError}
          tableRows={filteredRows}
          t={t}
        />
      </div>
    </div>
  );
}

/**
 * Visitor configuration tab for custom field admin (list view).
 *
 * Tab strip visibility is gated by {@link FeatureFlag.VisitorManagementCustomFields} on
 * {@link VisitorConfigurationPage}. Create and detail flows ship in a follow-up PR; the
 * list body therefore renders rows as read-only and omits the empty-state Create button.
 */
export function VisitorManagementCustomFieldsTab({
  channel,
  config: _config,
  onConfigUpdated: _onConfigUpdated,
  loading: _loading,
  showValidationErrors: _showValidationErrors,
}: VisitorConfigurationTabProps): React.ReactElement {
  const {
    t,
    searchValue,
    setSearchValue,
    filteredRows,
    isListLoading,
    configsCalled,
    configsError,
  } = useCustomFieldsTabModel({ channel });

  if (!channel?._id) {
    return (
      <div className={styles.container}>
        <div className={styles.section}>
          <P className={styles.noOrganizationMessage}>
            {t(NO_ORGANIZATION_KEY)}
          </P>
        </div>
      </div>
    );
  }

  return (
    <CustomFieldsListLayout
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      filteredRows={filteredRows}
      isListLoading={isListLoading}
      configsCalled={configsCalled}
      configsError={configsError}
    />
  );
}
